import React from 'react';
import Field from '../../Field';
import { STUDENT_NOTE_MAX, STUDENT_TEXT_MAX } from '../../../domain/people';
import { TAG_KINDS, TAG_KIND_LABELS } from '../../../domain/tags';
import TagPickerField from '../TagPickerField';
import { Radio } from '../../FormControls';
// The status set lives in columns.js, shared with the edit drawer and the
// list's filter (#177).
import { STATUSES, STATUS_LABELS } from '../columns';
import s from './addStudent.module.css';
import { DateInput } from '../../RegionInputs';

// Step 1. Everything above "Additional Details" is what MMS asks for up front;
// the rest is collapsed, because a teacher adding a student mid-phone-call
// should not have to scroll past a dozen optional fields to reach Next.
//
// ── No email, phone or Student Lesson Reminders (#792) ─────────────────────
//
// MMS's Student Details asks for the student's own Email, Phone and SMS
// Capable, and under Additional Details "Student Lesson Reminders". Here a
// student is not a message destination: every message goes to the family's
// contacts, and a contact's own ticks decide its lesson reminders. So the
// address, the number and the ticks are asked on the Family step, for the
// contact it creates — an adult's own, for an adult (FamilyStep.jsx,
// core/src/add_student.rs).
//
// The student's time zone is not here either: MMS asks for it on the Family
// Details step, because one zone applies to the whole family account
// (FamilyStep.jsx, domain/people.js).
export default function StudentDetailsStep({
  form, set, showMore, onToggleMore,
  // The studio's managed tags, and the two ways step 1 changes what this
  // student will be filed under (#138). Passed to `AdditionalDetails`, the
  // disclosure the pickers live in, as MMS puts them under `Additional
  // Details`. All five come from `useAddStudentForm`, where the membership
  // write lives.
  tagPicker = {},
}) {
  return (
    <>
      <div className={s.groupLabel}>Student Type</div>
      <Radio
        name="stype" label="Child"
        checked={form.studentType === 'child'}
        onChange={() => set('studentType', 'child')}
      />
      <Radio
        name="stype" label="Adult"
        checked={form.studentType === 'adult'}
        onChange={() => set('studentType', 'adult')}
      />

      <div className={[s.spaced, s.narrow].join(' ')}>
        {/* `htmlFor`/`id`: `Field` renders a bare <label> without one, which
            leaves the select unlabelled for a screen reader. */}
        <Field label="Status" htmlFor="add-student-status">
          <select
            id="add-student-status" className={s.input}
            value={form.status} onChange={(e) => set('status', e.target.value)}
          >
            {STATUSES.map((value) => (
              <option key={value} value={value}>{STATUS_LABELS[value]}</option>
            ))}
          </select>
        </Field>
      </div>

      {/* `maxLength` on all three (#120, #121): firestore.rules bounds these
          fields, and a pasted name past 200 characters would otherwise fail as
          a bare permission error naming no field (#115). The numbers are
          STUDENT_TEXT_MAX's, one short of each clause. */}
      <div className={s.pair}>
        <Field label="First Name" required>
          <input
            className={s.input} maxLength={STUDENT_TEXT_MAX.firstName}
            value={form.firstName} onChange={(e) => set('firstName', e.target.value)}
          />
        </Field>
        <Field label="Last Name" required>
          <input
            className={s.input} maxLength={STUDENT_TEXT_MAX.lastName}
            value={form.lastName} onChange={(e) => set('lastName', e.target.value)}
          />
        </Field>
      </div>

      <button className={s.disclosure} onClick={onToggleMore}>
        Additional Details <span className={s.disclosureNote}>(Optional)</span>
        <span className={s.disclosureChevron}>{showMore ? '▴' : '▾'}</span>
      </button>

      {showMore ? (
        <AdditionalDetails form={form} set={set} {...tagPicker} />
      ) : null}
    </>
  );
}

function AdditionalDetails({
  form, set,
  tags = [], tagsOfKind = () => [], selectedTagIds = new Set(),
  onToggleTag = () => {}, onCreateTag = () => {}, tagsLoaded = true,
}) {
  // `max` is STUDENT_TEXT_MAX's for the four free-text fields (#120, #121) and
  // absent for the two dates, which are date boxes in the studio's Region
  // (#526) and carry a shape rather than a length.
  const text = (key, label, hint, type = 'text') => (
    <Field label={label} hint={hint}>
      {type === 'date' ? (
        <DateInput className={s.input} value={form[key]} onChange={(e) => set(key, e.target.value)} />
      ) : (
        <input
          type={type} className={s.input} maxLength={STUDENT_TEXT_MAX[key]}
          value={form[key]} onChange={(e) => set(key, e.target.value)}
        />
      )}
    </Field>
  );

  // ── The two tag pickers (#138) ────────────────────────────────────────────
  //
  // A picker over the studio's managed tags, as MMS has here: `Instrument
  // Tags` and `Group Tags`, each with `Create Instrument Tag` / `Create Group
  // Tag` inline and `Go to Tags` beside it (SPEC.md §1.2) — the three-part
  // control `Category` has (#110). A typed string would sit outside the managed
  // list, unreachable by a rename or a recolour.
  //
  // Not a `<datalist>` over a comma-separated box: a datalist filters and
  // selects against the input's entire value, so suggestions stop at the first
  // comma and accepting one replaces everything typed. `TagPickerField` treats
  // each tag as its own value.
  //
  // The bound on a membership is the tag's own `studentIds`, which
  // `firestore.rules` caps at 500. Nothing in this app writes the student
  // document's `instrumentTags` / `groupTags` (#328); `studentTagLabels` in
  // domain/tags.js still reads both sources for a studio that has not run
  // `scripts/migrate-tags.mjs`.

  return (
    <div className={s.spaced}>
      <div className={s.pair}>{text('studentSince', 'Student Since', 'Optional', 'date')}</div>
      <div className={s.pair}>
        {text('gender', 'Gender', 'Optional')}
        {text('birthday', 'Birthday', 'Optional', 'date')}
      </div>
      <div className={s.pair}>
        {text('school', 'School', 'Optional')}
        {text('referrer', 'Referrer', 'Optional')}
      </div>
      <div className={s.spaced}>{text('skillLevel', 'Skill Level', 'Optional')}</div>
      <div className={s.spaced}>
        <TagPickerField
          label="Instrument Tags"
          kind={TAG_KINDS.instrument}
          kindLabel={`${TAG_KIND_LABELS[TAG_KINDS.instrument]} Tag`}
          tags={tagsOfKind(TAG_KINDS.instrument)}
          allTags={tags}
          loaded={tagsLoaded}
          selected={selectedTagIds}
          onToggle={onToggleTag}
          onCreate={(label) => onCreateTag(label, TAG_KINDS.instrument)}
        />
      </div>
      <div className={s.spaced}>
        <TagPickerField
          label="Group Tags"
          kind={TAG_KINDS.group}
          kindLabel={`${TAG_KIND_LABELS[TAG_KINDS.group]} Tag`}
          tags={tagsOfKind(TAG_KINDS.group)}
          allTags={tags}
          loaded={tagsLoaded}
          selected={selectedTagIds}
          onToggle={onToggleTag}
          onCreate={(label) => onCreateTag(label, TAG_KINDS.group)}
        />
      </div>
      <div className={s.spaced}>
        <Field label="Note" hint="Visible to teachers and admins, not to parents or students">
          {/* Bounded here as well as in the rules (#115): `normaliseStudent`
              does not truncate, so a paste past the limit would be a save that
              did nothing. STUDENT_NOTE_MAX carries the arithmetic.

              No character counter: as MMS's editor shows none where a note has
              no length to count against (#93), and at 65,535 a number would
              advertise a limit nothing a teacher types can reach. */}
          <textarea
            rows={3} className={s.input} maxLength={STUDENT_NOTE_MAX}
            value={form.note} onChange={(e) => set('note', e.target.value)}
          />
        </Field>
      </div>
    </div>
  );
}
